/**
 * English copy for offline attendance
 * (`specs/offline-attendance-v1/implementation-plan.md`): punch and status
 * labels, toasts, readiness explanations, sync phases, result-code
 * explanations, capture refusals, and the banner / badge / menu / sign-in /
 * Sync-page strings.
 *
 * Same shape as `notifications.en.ts` — a flat, typed key → string map with no
 * i18n library (see CLAUDE.md, keep the lean stack). Only `en` is selectable
 * today, so this is the sole locale. Templates carry `{count}` / `{max}`
 * placeholders that only the helpers below fill in.
 *
 * Completeness is checked by the compiler: the map `satisfies` a record of
 * every key derived from the contract's code lists and the app's readiness /
 * sync / status unions, so adding a reason or a code without its copy is a
 * type error, not a blank label at run time.
 */
import type {
  AttendanceEventType,
  DeferralCode,
  RejectionCode,
  WholeRequestErrorCode,
} from '@/contracts/offline-attendance-v1';
import type {
  CaptureOutcome,
  OfflineReadinessReason,
  OfflineReadinessState,
  ProjectedState,
  SyncPauseReason,
  SyncPhase,
} from '@/types/offline-attendance';

/** Why a capture was refused (the reasons a `refused` CaptureOutcome can carry). */
export type CaptureRefusalReason = Extract<CaptureOutcome, { kind: 'refused' }>['reason'];

/** The refusals that are not a readiness reason (those reuse the readiness copy). */
type LocalRefusalReason = Exclude<CaptureRefusalReason, OfflineReadinessReason>;

/** The status a punch row or pill shows. */
export type PunchStatus = 'pending' | 'syncing' | 'needs_review' | 'confirmed' | 'sign_in_to_sync';

/**
 * Codes the client itself records on an event it will resend (transport and
 * unresolved-answer outcomes — ERPat never sends these per event). Each has
 * copy, so a pending row never shows the generic "Ask HR" text for an outage.
 */
export type ClientSyncCode =
  | 'network_error'
  | 'timeout'
  | 'security_challenge'
  | 'cancelled'
  | 'refresh_failed'
  | 'session_expired'
  | 'no_session'
  | 'invalid_response'
  | 'unresolved';

/**
 * The two `result.*` sentences that are NOT a code: what an unknown code reads
 * as on a final punch (`generic`) and on a punch that is still waiting
 * (`genericRetry`). A code that happens to carry one of these names is still
 * an unknown code (see {@link resultCodeCopy}).
 */
const RESULT_FALLBACKS = ['generic', 'genericRetry'] as const;
type ResultFallback = (typeof RESULT_FALLBACKS)[number];

type RequiredCopyKey =
  | `event.${AttendanceEventType}`
  | `toast.confirmed.${AttendanceEventType}`
  | `status.${PunchStatus}`
  | `shift.${ProjectedState}`
  | `readiness.state.${OfflineReadinessState}.${'title' | 'body'}`
  | `readiness.reason.${OfflineReadinessReason}.${'title' | 'body'}`
  | `sync.phase.${SyncPhase}`
  | `sync.paused.${SyncPauseReason}`
  | `result.${RejectionCode | DeferralCode | WholeRequestErrorCode | ClientSyncCode | ResultFallback | 'accepted'}`
  | `capture.${LocalRefusalReason}`;

export const offlineAttendanceEn = {
  // ── Actions ────────────────────────────────────────────────────────────
  'event.time_in': 'Time In',
  'event.time_out': 'Time Out',
  'event.break_out': 'Break Out',
  'event.break_in': 'Break In',

  // ── Toasts ─────────────────────────────────────────────────────────────
  /** Shown only after a valid ERPat receipt (or a successful online punch). */
  'toast.confirmed.time_in': 'Clocked In',
  'toast.confirmed.time_out': 'Clocked Out',
  'toast.confirmed.break_out': 'Break started',
  'toast.confirmed.break_in': 'Break ended',
  /** Shown right after the punch is committed on the device, before any upload. */
  'toast.pendingSaved': 'Punch saved — waiting to sync',

  // ── Punch status pills ─────────────────────────────────────────────────
  'status.pending': 'Pending',
  'status.syncing': 'Syncing',
  'status.needs_review': 'Needs review',
  'status.confirmed': 'Confirmed',
  'status.sign_in_to_sync': 'Sign in to sync',

  // ── Shift state from the projection ────────────────────────────────────
  'shift.off_shift': 'Not clocked in',
  'shift.working': 'Working',
  'shift.on_break': 'On break',
  /** Never "Rest day": an unknown state means the shift could not be read. */
  'shift.unknown': 'Shift status unavailable',
  'shift.doneForToday': 'Shift completed',
  /** Appended (after a space) while the state rests on punches not yet confirmed. */
  'shift.pendingSuffix': '· pending',

  // ── Clock controls while the device projection cannot take a punch ─────
  /** A punch waits for HR (it was refused, or follows one that was): nothing new can be added on top. */
  'clock.blocked.review': 'A punch needs review — open Sync to see it.',
  /** ERPat already holds the last punch, or its answer is being checked: the resend settles it. */
  'clock.blocked.syncing': 'Finishing your last punch — try again in a moment.',
  /** The title of the disabled control while the shift state cannot be read. */
  'clock.blocked.unknownTitle': 'Checking…',

  // ── Recent Activity / Sync page rows ───────────────────────────────────
  /** An unsettled punch held behind one ERPat refused. */
  'review.heldBehind': 'Held because an earlier punch needs review.',

  // ── Readiness: per state (used when there is no reason) ────────────────
  'readiness.state.ready.title': 'Ready to save punches offline',
  'readiness.state.ready.body':
    "If your connection drops, Syntry saves your punches on this device and sends them as soon as it can reach your company's server.",
  'readiness.state.preparing.title': 'Getting offline punches ready',
  'readiness.state.preparing.body': 'Syntry is preparing this device to save punches when your connection drops.',
  'readiness.state.degraded.title': 'Offline punches need attention',
  'readiness.state.degraded.body':
    'Punches still save on this device, but something needs your attention so they keep syncing.',
  'readiness.state.blocked.title': "Punches can't be saved right now",
  'readiness.state.blocked.body': "Syntry can't save new punches on this device at the moment.",
  'readiness.state.online_only.title': 'Punches need a connection',
  'readiness.state.online_only.body': "Clock in and out while you're online — punches aren't saved on this device.",

  // ── Readiness: per reason ──────────────────────────────────────────────
  'readiness.reason.build_disabled.title': "Offline punches aren't available",
  'readiness.reason.build_disabled.body':
    "This version of Syntry sends punches only while you're connected. You can still clock in and out while you're online.",
  'readiness.reason.native_disabled.title': "Offline punches aren't available in the app yet",
  'readiness.reason.native_disabled.body':
    "On this device, punches are sent only while you're connected. You can still clock in and out while you're online.",
  'readiness.reason.unsupported_environment.title': "Offline punches aren't supported here",
  'readiness.reason.unsupported_environment.body':
    "This browser can't store punches securely. Clock in and out while you're online, or use a current browser or the Syntry app.",
  'readiness.reason.unsupported_backend.title': "Offline punches aren't available yet",
  'readiness.reason.unsupported_backend.body':
    "Your company's server doesn't support offline punches yet. You can still clock in and out while you're online.",
  'readiness.reason.disabled_by_company.title': 'Offline punches are turned off',
  'readiness.reason.disabled_by_company.body':
    "Your company hasn't turned on offline punches. You can still clock in and out while you're online.",
  'readiness.reason.evidence_required.title': 'Connection needed to clock in or out',
  'readiness.reason.evidence_required.body':
    'Your company requires a photo or location with each punch, so you can only clock in and out while online.',
  'readiness.reason.permission_missing.title': "Clocking in isn't enabled for you",
  'readiness.reason.permission_missing.body':
    "Your account doesn't have permission to clock in or out in Syntry. Ask HR if you need it.",
  'readiness.reason.not_provisioned.title': 'Connect once to set up offline punches',
  'readiness.reason.not_provisioned.body':
    'Go online once so Syntry can prepare this device to save punches when your connection drops.',
  'readiness.reason.provisioning.title': 'Setting up offline punches…',
  'readiness.reason.provisioning.body':
    'Syntry is preparing this device to save punches offline. This only takes a moment.',
  'readiness.reason.signed_out.title': 'Sign in to clock in or out',
  'readiness.reason.signed_out.body':
    'Sign in again to save new punches and send the ones waiting on this device.',
  'readiness.reason.grant_expired.title': 'Connect to keep saving punches offline',
  'readiness.reason.grant_expired.body':
    "This device hasn't checked in with your company for too long, so new offline punches are paused. Go online to continue — punches already saved will still sync.",
  'readiness.reason.clock_unsafe.title': "Check your device's date and time",
  'readiness.reason.clock_unsafe.body':
    "Your device's clock changed, so Syntry can't record an accurate punch time. Set the date and time to automatic, then go online to continue.",
  'readiness.reason.storage_unavailable.title': "Can't save punches on this device",
  'readiness.reason.storage_unavailable.body':
    "Syntry can't write to this device's storage, so new punches can't be saved. Free up some space, then reopen Syntry.",
  // A device whose storage fails BEFORE anything was ever saved there still clocks
  // in and out online (readiness online_only/storage_unavailable, plan amendment
  // C23) — so this pair says so instead of the blocked "can't be saved" copy.
  'readiness.online_only.storage_unavailable.title': "Punches can't be saved on this device",
  'readiness.online_only.storage_unavailable.body':
    "This device's storage isn't working, so punches can't wait here for a connection. You can still clock in and out while you're online.",
  'readiness.reason.key_unavailable.title': "Saved punches can't be unlocked",
  'readiness.reason.key_unavailable.body':
    "Syntry lost access to the key that protects punches on this device. Saved punches are kept, but new ones can't be saved. Contact HR or your administrator.",
  'readiness.reason.ambiguous_attendance.title': 'Your attendance needs a check',
  'readiness.reason.ambiguous_attendance.body':
    'You have more than one open shift. Ask HR to fix your attendance before you clock in or out again.',
  'readiness.reason.queue_waiting.title': 'Punches are waiting to sync',
  'readiness.reason.queue_waiting.body':
    "Some punches saved on this device haven't been sent yet. Go online so they can sync before you clock in or out again.",
  // Raised for THIS account's own offline records (their employee id or pinned
  // issuer no longer match) — another account's punches live under another
  // scope and never produce it (plan amendment C7).
  'readiness.reason.scope_mismatch.title': 'Offline punches need to be set up again',
  'readiness.reason.scope_mismatch.body':
    "This device's offline data no longer matches your account. Go online so Syntry can set it up again — punches already saved stay on this device.",
  'readiness.reason.refresh_due.title': 'Go online soon to keep offline punches working',
  'readiness.reason.refresh_due.body':
    "Syntry hasn't checked in with your company for a while. Connect to the internet so offline punches keep working.",
  'readiness.reason.storage_not_persistent.title': 'Your browser may clear saved punches',
  'readiness.reason.storage_not_persistent.body':
    "This browser may clear Syntry's storage when space runs low. Sync often, or install Syntry on your home screen to keep punches safer.",
  'readiness.reason.sync_failing.title': "Punches haven't synced for a while",
  'readiness.reason.sync_failing.body':
    "Syntry hasn't been able to send your saved punches for over an hour. They'll sync automatically once your connection is back.",

  // ── Sync phase ─────────────────────────────────────────────────────────
  'sync.phase.idle': 'Up to date',
  'sync.phase.syncing': 'Syncing punches…',
  'sync.phase.waiting': 'Waiting to try again',
  'sync.phase.offline': 'Waiting for a connection',
  'sync.phase.paused': 'Sync paused',
  'sync.paused.signed_out': 'Sync paused — sign in to send your punches',
  'sync.paused.permission': "Sync paused — your account can't clock in or out in Syntry",
  'sync.paused.module_unavailable': 'Sync paused — attendance is turned off for your company',
  'sync.paused.account_inactive': 'Sync paused — your account is inactive',
  'sync.paused.unsupported': "Sync paused — your company's server can't receive offline punches",
  'sync.paused.protocol_error': "Sync paused — the server couldn't read your punches. Update Syntry.",
  'sync.paused.storage': "Sync paused — this device's storage isn't working",
  'sync.paused.key': "Sync paused — saved punches can't be unlocked on this device",

  // ── Result codes: accepted + final rejections ──────────────────────────
  'result.accepted': 'Saved to your attendance.',
  'result.invalid_event': "This punch couldn't be read.",
  'result.grant_missing': 'This punch was saved without valid offline permission.',
  'result.grant_invalid': 'This punch was saved without valid offline permission.',
  'result.grant_revoked': 'Offline permission for this device was withdrawn.',
  'result.grant_expired': 'This punch was saved after offline permission expired.',
  'result.too_old': 'This punch is too old to sync.',
  'result.future_event': 'This punch has a time in the future.',
  'result.sequence_conflict': "This punch's order on the device doesn't match its time.",
  'result.sequence_time_conflict': "This punch's order on the device doesn't match its time.",
  'result.dependency_rejected': "An earlier punch in this shift wasn't accepted.",
  'result.dependency_missing': 'An earlier punch in this shift is missing.',
  'result.dependency_mismatch': "An earlier punch in this shift doesn't match your attendance record.",
  'result.state_conflict': 'Your attendance changed since this punch was saved.',
  'result.open_shift_exists': 'You were already clocked in.',
  'result.multiple_open_shifts': 'You have more than one open shift.',
  'result.overlap': 'This shift overlaps another attendance record.',
  'result.out_of_order': 'This punch is earlier than your last recorded punch.',
  'result.no_schedule': 'No schedule covers the day of this punch.',
  'result.date_locked': 'That attendance date is locked.',
  'result.shift_not_open': 'That shift is already closed.',
  'result.on_break': 'You were on a break.',
  'result.not_on_break': "You weren't on a break.",
  'result.break_limit': "You've reached the most breaks allowed for a shift.",
  'result.before_shift_start': 'This punch is before your shift started.',
  'result.span_too_long': 'This shift is longer than 24 hours.',
  'result.note_required': 'A note is required to clock out.',
  'result.event_id_conflict': 'This punch conflicts with a punch already received.',

  // ── Result codes: deferrals (retried with the same punch) ──────────────
  'result.lock_busy': 'The server was busy. Syntry will try again.',
  'result.lock_unavailable': "The server couldn't process this punch yet. Syntry will try again.",
  'result.earlier_event_deferred': 'Waiting for an earlier punch to sync first.',
  'result.state_changed': 'Your attendance was changing on the server. Syntry will try again.',
  'result.commit_failed': "The server couldn't save this punch yet. Syntry will try again.",
  'result.server_error': 'The server had a problem. Syntry will try again.',
  'result.time_budget': 'The server ran out of time. Syntry will send this punch again.',

  // ── Result codes: whole-request refusals ───────────────────────────────
  'result.module_unavailable': 'Attendance is turned off for your company.',
  'result.forbidden': "Your account can't clock in or out in Syntry.",
  'result.account_inactive': 'Your account is inactive.',
  'result.validation_failed': "The server couldn't read these punches. Update Syntry.",
  'result.unsupported_schema': "Your company's server can't receive punches from this version of Syntry.",
  'result.offline_sync_unavailable':
    "Your company's server isn't ready to receive offline punches yet. Syntry will try again.",
  'result.rate_limited': 'Too many requests. Syntry will try again shortly.',

  // ── Result codes: client-side outcomes (the punch is resent) ───────────
  'result.network_error': "Couldn't reach the server. Syntry will try again.",
  'result.timeout': 'The server took too long to answer. Syntry will try again.',
  'result.security_challenge': 'A security check blocked the connection. Syntry will try again.',
  'result.cancelled': 'Sending was interrupted. Syntry will try again.',
  'result.refresh_failed': "Couldn't renew your sign-in. Syntry will try again.",
  'result.session_expired': 'Sign in again to send this punch.',
  'result.no_session': 'Sign in to send this punch.',
  'result.invalid_response': "The server's answer couldn't be read. Syntry will try again.",
  'result.unresolved': "The server's answer didn't confirm this punch. Syntry will send it again.",

  /** Any code this build does not know, on a FINAL (settled) punch. */
  'result.generic': "ERPat couldn't accept this punch. Ask HR to review it.",
  /** Any code this build does not know, on a punch that is still waiting — it is retried (plan amendment C6). */
  'result.genericRetry': 'Waiting to sync — Syntry will try again.',

  // ── Capture refusals (nothing was queued on the device) ────────────────
  'capture.busy': 'Hold on — your last punch is still being saved.',
  'capture.not_allowed': "That action isn't available right now. Check your shift status and try again.",
  'capture.note_required': 'Add a clock-out note before ending your workday.',
  'capture.note_too_long': 'Your clock-out note is too long. Shorten it and try again.',
  'capture.noteTooLongLimit': 'Your clock-out note is too long — keep it to {max} characters or fewer.',
  'capture.storage':
    "Your punch wasn't saved. This device's storage isn't working — free up some space and try again.",
  'capture.state_changed': 'Your attendance just changed on another screen. Check your status and try again.',
  // The online path is the legacy, non-idempotent POST: after a timeout or a lost
  // response ERPat may already have the punch, so this neither says it was not
  // recorded nor invites a blind retry (plan amendment C7, Review Focus 1).
  'capture.online_failed':
    "Syntry couldn't confirm this punch with ERPat. Check your shift status before trying again.",

  // ── Offline banner ─────────────────────────────────────────────────────
  'banner.offline': "You're offline — showing saved data.",
  'banner.blocked': 'Blocked by a security check — showing saved data.',
  'banner.offlinePending.one': "You're offline — {count} punch waiting to sync.",
  'banner.offlinePending.other': "You're offline — {count} punches waiting to sync.",

  // ── Sync badge ─────────────────────────────────────────────────────────
  'badge.pending': '{count} pending',
  'badge.pendingLabel.one': '{count} punch waiting to sync',
  'badge.pendingLabel.other': '{count} punches waiting to sync',
  'badge.review.one': '{count} needs review',
  'badge.review.other': '{count} need review',
  'badge.reviewLabel.one': '{count} punch needs review',
  'badge.reviewLabel.other': '{count} punches need review',
  /** Ends the badge's accessible name: where the badge leads. */
  'badge.open': 'Open Sync',

  // ── Side menu ──────────────────────────────────────────────────────────
  'menu.sync': 'Sync',
  'menu.signOutPending.one':
    "{count} punch hasn't synced yet. It stays on this device and will sync when you sign in again with this account.",
  'menu.signOutPending.other':
    "{count} punches haven't synced yet. They stay on this device and will sync when you sign in again with this account.",

  // ── Sign-in page ───────────────────────────────────────────────────────
  'signIn.punchesWaiting':
    'Punches saved on this device are waiting to sync. Sign in with the account that saved them to send them.',

  // ── Sync page ──────────────────────────────────────────────────────────
  'syncIssues.title': 'Sync',
  'syncIssues.readinessHeading': 'Offline punches',
  'syncIssues.syncHeading': 'Sync status',
  'syncIssues.pendingLabel': 'Waiting to sync',
  'syncIssues.reviewLabel': 'Needs review',
  'syncIssues.oldestLabel': 'Oldest waiting',
  'syncIssues.lastSuccessLabel': 'Last synced',
  'syncIssues.never': 'Never',
  'syncIssues.syncNow': 'Sync now',
  'syncIssues.syncing': 'Syncing…',
  'syncIssues.eventsHeading': 'Punches',
  'syncIssues.empty': 'Nothing is waiting to sync.',
  'syncIssues.today': 'Today',
  'syncIssues.earlier': 'Earlier',
  'syncIssues.askHr': 'Ask HR to correct this.',
  'syncIssues.technicalDetails': 'Technical details',
  'syncIssues.punchId': 'Punch ID',
  'syncIssues.deviceSequence': 'Device sequence',
  'syncIssues.deviceId': 'Device ID',
  'syncIssues.phaseLabel': 'Status',
} as const satisfies Record<RequiredCopyKey, string> & Record<string, string>;

export type OfflineAttendanceCopyKey = keyof typeof offlineAttendanceEn;

/** Look up one string by its catalogue key (a typo is a compile error). */
export function offlineAttendanceCopy(key: OfflineAttendanceCopyKey): string {
  return offlineAttendanceEn[key];
}

/** `key` names a string in the catalogue (own keys only — never `toString` and friends). */
function hasCopy(key: string): key is OfflineAttendanceCopyKey {
  return Object.prototype.hasOwnProperty.call(offlineAttendanceEn, key);
}

function fill(template: string, token: '{count}' | '{max}', value: number): string {
  return template.replace(token, String(value));
}

/** Pick the singular or plural template for `count` and fill in the count. */
function counted(one: OfflineAttendanceCopyKey, other: OfflineAttendanceCopyKey, count: number): string {
  return fill(offlineAttendanceEn[count === 1 ? one : other], '{count}', count);
}

/** The time-free confirmation banner: "Punch saved — waiting to sync". */
export const pendingSavedCopy = offlineAttendanceEn['toast.pendingSaved'];

/** Time In / Time Out / Break Out / Break In. */
export function eventTypeLabel(type: AttendanceEventType): string {
  return offlineAttendanceEn[`event.${type}`];
}

/** The toast after ERPat confirms the action: Clocked In / Clocked Out / Break started / Break ended. */
export function confirmedToastCopy(type: AttendanceEventType): string {
  return offlineAttendanceEn[`toast.confirmed.${type}`];
}

/** The saved-but-unsynced toast: "Punch saved — waiting to sync · Break Out 12:00 PM". */
export function pendingSavedToastCopy(type: AttendanceEventType, timeLabel: string): string {
  return `${pendingSavedCopy} · ${eventTypeLabel(type)} ${timeLabel}`;
}

export function punchStatusLabel(status: PunchStatus): string {
  return offlineAttendanceEn[`status.${status}`];
}

/** "Working", or "Working · pending" while the state rests on unconfirmed punches. */
export function shiftStateLabel(state: ProjectedState, projected: boolean): string {
  const label = offlineAttendanceEn[`shift.${state}`];
  return projected ? `${label} ${offlineAttendanceEn['shift.pendingSuffix']}` : label;
}

/**
 * Title and body explaining offline readiness. A reason explains itself the
 * same way whatever the state; with no reason (only `ready` has none) — or a
 * reason this build does not know — the state's own copy is used.
 */
export function readinessCopy(
  state: OfflineReadinessState,
  reason: OfflineReadinessReason | null,
): { title: string; body: string } {
  if (reason !== null) {
    // A state-specific pair wins over the reason's general one (online_only/storage_unavailable).
    const pairTitle: string = `readiness.${state}.${reason}.title`;
    const pairBody: string = `readiness.${state}.${reason}.body`;
    if (hasCopy(pairTitle) && hasCopy(pairBody)) {
      return { title: offlineAttendanceEn[pairTitle], body: offlineAttendanceEn[pairBody] };
    }
    const title: string = `readiness.reason.${reason}.title`;
    const body: string = `readiness.reason.${reason}.body`;
    if (hasCopy(title) && hasCopy(body)) return { title: offlineAttendanceEn[title], body: offlineAttendanceEn[body] };
  }
  const stateTitle: string = `readiness.state.${state}.title`;
  const stateBody: string = `readiness.state.${state}.body`;
  if (hasCopy(stateTitle) && hasCopy(stateBody)) {
    return { title: offlineAttendanceEn[stateTitle], body: offlineAttendanceEn[stateBody] };
  }
  return {
    title: offlineAttendanceEn['readiness.state.blocked.title'],
    body: offlineAttendanceEn['readiness.state.blocked.body'],
  };
}

/** One line describing the sync engine; a paused engine says why. */
export function syncPhaseCopy(phase: SyncPhase, pauseReason: SyncPauseReason | null): string {
  if (phase === 'paused' && pauseReason !== null) {
    const key: string = `sync.paused.${pauseReason}`;
    if (hasCopy(key)) return offlineAttendanceEn[key];
  }
  return offlineAttendanceEn[`sync.phase.${phase}`];
}

/** How {@link resultCodeCopy} reads a code it does not know. */
export interface ResultCodeCopyOptions {
  /**
   * Whether the punch carrying the code is settled. `true` (the default) for a
   * final row; `false` for one still waiting to sync — ERPat may defer with a
   * code this build does not know, and that punch is retried, not refused.
   */
  final?: boolean;
}

function isResultFallback(code: string): code is ResultFallback {
  return (RESULT_FALLBACKS as readonly string[]).includes(code);
}

/**
 * What a result (or requeue) code means for the employee. Every contract code
 * and client-side outcome has its own sentence, whatever `final` says. A code
 * this build does not know reads, on a final punch (the default), "ERPat
 * couldn't accept this punch. Ask HR to review it." and, with
 * `{ final: false }`, "Waiting to sync — Syntry will try again." (plan
 * amendment C6). A code named like one of those two fallback entries is
 * unknown too, so it can never borrow the other context's sentence.
 */
export function resultCodeCopy(code: string, opts: ResultCodeCopyOptions = {}): string {
  const key = `result.${code}`;
  if (!isResultFallback(code) && hasCopy(key)) return offlineAttendanceEn[key];
  return offlineAttendanceEn[opts.final === false ? 'result.genericRetry' : 'result.generic'];
}

function isLocalRefusal(reason: CaptureRefusalReason): reason is LocalRefusalReason {
  return hasCopy(`capture.${reason}`);
}

/**
 * Why a capture was refused — nothing was queued on the device (after
 * `online_failed` ERPat's outcome is unknown, so its copy claims neither way).
 * A readiness reason is explained exactly as the readiness card explains it.
 *
 * @param opts.noteMaxLength the policy's note limit, named in the
 *   `note_too_long` message when known.
 */
export function captureRefusalCopy(reason: CaptureRefusalReason, opts: { noteMaxLength?: number } = {}): string {
  if (reason === 'note_too_long' && opts.noteMaxLength !== undefined && Number.isFinite(opts.noteMaxLength)) {
    return fill(offlineAttendanceEn['capture.noteTooLongLimit'], '{max}', opts.noteMaxLength);
  }
  if (isLocalRefusal(reason)) return offlineAttendanceEn[`capture.${reason}`];
  return readinessCopy('blocked', reason).body;
}

/**
 * The offline banner line. The security-check copy wins (the connection is
 * fine); otherwise waiting punches are counted, else the plain offline line.
 */
export function offlineBannerCopy(input: { blocked: boolean; pendingCount: number }): string {
  if (input.blocked) return offlineAttendanceEn['banner.blocked'];
  if (input.pendingCount > 0) {
    return counted('banner.offlinePending.one', 'banner.offlinePending.other', input.pendingCount);
  }
  return offlineAttendanceEn['banner.offline'];
}

/** The Sync badge: visible text ("2 pending") and its accessible label. */
export function syncBadgeCopy(kind: 'pending' | 'review', count: number): { text: string; label: string } {
  if (kind === 'pending') {
    return {
      text: fill(offlineAttendanceEn['badge.pending'], '{count}', count),
      label: counted('badge.pendingLabel.one', 'badge.pendingLabel.other', count),
    };
  }
  return {
    text: counted('badge.review.one', 'badge.review.other', count),
    label: counted('badge.reviewLabel.one', 'badge.reviewLabel.other', count),
  };
}

/** The sign-out confirmation's disclosure of punches that have not synced yet. */
export function signOutPendingCopy(count: number): string {
  return counted('menu.signOutPending.one', 'menu.signOutPending.other', count);
}
